import { type FC, useState } from 'react'
import { Form } from 'react-bootstrap'
import { useDebouncedCallback } from 'use-debounce'
import { Button } from '../../../../../components/Button/Button'
import { Modal } from '../../../../../components/Modal/Modal'
import { Spinner } from '../../../../../components/Spinner/Spinner'
import { type EventTypeHost, HostMode } from '../../../../../models/Scheduling'
import { useAppSelector } from '../../../../../store/hooks'
import { selectSafeAuthenticatedUser } from '../../../../../store/auth/authSlice'
import { useGetEventTypeHostCandidatesQuery, useLazyGetEventTypeHostCandidatesQuery } from '../../../../../store/scheduling/scheduling'
import { HostIdentity, HostStatus } from './HostRow'

const PAGE_SIZE = 5
const SELECT_ALL_PAGE_SIZE = 100
const SEARCH_DEBOUNCE_MS = 300

interface SelectHostsModalProps {
  eventTypeId: string
  hostMode: HostMode
  selected: EventTypeHost[]
  onSave: (hosts: EventTypeHost[]) => void
  onCancel: () => void
}

const sameHosts = (left: EventTypeHost[], right: EventTypeHost[]): boolean => {
  const leftIds = left.map((host) => host.userId).sort()
  const rightIds = right.map((host) => host.userId).sort()
  return leftIds.length === rightIds.length && leftIds.every((id, index) => id === rightIds[index])
}

export const SelectHostsModal: FC<SelectHostsModalProps> = ({ eventTypeId, hostMode, selected, onSave, onCancel }) => {
  const authenticatedUser = useAppSelector(selectSafeAuthenticatedUser)
  const accountId = authenticatedUser.accountId
  const isSingle = hostMode === HostMode.SINGLE

  const [searchInput, setSearchInput] = useState('')
  const [name, setName] = useState('')
  const [page, setPage] = useState(0)
  const [selectedHosts, setSelectedHosts] = useState<EventTypeHost[]>(selected)
  const [isSelectingAll, setIsSelectingAll] = useState(false)

  const { data, isFetching } = useGetEventTypeHostCandidatesQuery({ accountId, eventTypeId, name: name || undefined, page, size: PAGE_SIZE })
  const [fetchCandidates] = useLazyGetEventTypeHostCandidatesQuery()
  const candidates = data?.data ?? []
  const totalElements = data?.page.totalElements ?? 0
  const totalPages = data?.page.totalPages ?? 0
  const shownUpTo = Math.min((page + 1) * PAGE_SIZE, totalElements)

  const applySearch = useDebouncedCallback((value: string) => {
    setName(value.trim())
    setPage(0)
  }, SEARCH_DEBOUNCE_MS)

  const handleSearchChange = (value: string) => {
    setSearchInput(value)
    applySearch(value)
  }

  const isSelected = (userId: string) => selectedHosts.some((host) => host.userId === userId)

  const handleToggle = (candidate: EventTypeHost) => {
    setSelectedHosts((current) => {
      if (current.some((host) => host.userId === candidate.userId)) {
        return isSingle ? current : current.filter((host) => host.userId !== candidate.userId)
      }
      return isSingle ? [candidate] : [...current, candidate]
    })
  }

  const handleSelectAll = async () => {
    const term = searchInput.trim()
    const fetchPage = (pageIndex: number) =>
      fetchCandidates({ accountId, eventTypeId, name: term || undefined, page: pageIndex, size: SELECT_ALL_PAGE_SIZE }, true)
    setIsSelectingAll(true)
    try {
      const first = await fetchPage(0)
      if (!first.data) {
        return
      }
      const rest = await Promise.all(Array.from({ length: Math.max(first.data.page.totalPages - 1, 0) }, (_, index) => fetchPage(index + 1)))
      if (rest.some((result) => !result.data)) {
        return
      }
      const allCandidates = [first, ...rest].flatMap((result) => result.data?.data ?? [])
      setSelectedHosts((current) => [...current, ...allCandidates.filter((candidate) => !current.some((host) => host.userId === candidate.userId))])
    } finally {
      setIsSelectingAll(false)
    }
  }

  const canSave = !sameHosts(selectedHosts, selected) && (!isSingle || selectedHosts.length === 1)

  return (
    <Modal
      show
      onHide={onCancel}
      header={
        <div className='d-flex align-items-center zocks-gap-0.5'>
          <i className='bi bi-people-fill zocks-text-gray-900' aria-hidden='true' />
          <h5 className='mb-0'>{isSingle ? 'Choose a host' : 'Choose hosts'}</h5>
        </div>
      }
      body={
        <div className='d-flex flex-column gap-3'>
          <div className='d-flex align-items-center zocks-gap-1.25 border rounded-1 zocks-border-color-gray-400 zocks-px-0.75'>
            <i className='bi bi-search zocks-text-gray-500' aria-hidden='true' />
            <Form.Control
              type='search'
              autoComplete='off'
              value={searchInput}
              onChange={(event) => handleSearchChange(event.currentTarget.value)}
              placeholder='Search'
              aria-label='Search'
              className='border-0 px-0 paragraph-small'
            />
          </div>
          <div className='d-flex align-items-center justify-content-between px-3 py-2 rounded-2 zocks-bg-color-gray-100'>
            <span className='paragraph-small zocks-text-gray-700'>{isFetching && !data ? 'Loading…' : `Showing ${shownUpTo} out of ${totalElements}`}</span>
            <div className='d-flex align-items-center gap-1'>
              <Button variant='grayButton' className='p-1' aria-label='Previous page' disabled={page === 0} onClick={() => setPage((current) => current - 1)}>
                <i className='bi bi-chevron-left' aria-hidden='true' />
              </Button>
              <Button
                variant='grayButton'
                className='p-1'
                aria-label='Next page'
                disabled={page + 1 >= totalPages}
                onClick={() => setPage((current) => current + 1)}
              >
                <i className='bi bi-chevron-right' aria-hidden='true' />
              </Button>
            </div>
          </div>
          {isFetching && !data ? (
            <div className='d-flex justify-content-center py-4'>
              <Spinner />
            </div>
          ) : (
            <div className='d-flex flex-column'>
              {candidates.map((candidate) => {
                const displayName = candidate.name ?? candidate.email ?? candidate.userId
                const label = candidate.userId === authenticatedUser.id ? `${displayName} (You)` : displayName
                const inputId = `select-host-${candidate.userId}`
                return (
                  <div
                    key={candidate.userId}
                    className='d-flex align-items-center justify-content-between gap-3 py-3 border-bottom zocks-border-color-gray-200'
                  >
                    <div className='d-flex align-items-center gap-4'>
                      <Form.Check
                        type={isSingle ? 'radio' : 'checkbox'}
                        name={isSingle ? 'select-host' : undefined}
                        id={inputId}
                        checked={isSelected(candidate.userId)}
                        onChange={() => handleToggle(candidate)}
                        aria-label={label}
                      />
                      <label htmlFor={inputId} className='d-flex'>
                        <HostIdentity host={candidate} displayName={label} />
                      </label>
                    </div>
                    <HostStatus host={candidate} />
                  </div>
                )
              })}
            </div>
          )}
          {isSingle ? null : (
            <div>
              <Button
                type='button'
                variant='link'
                className='p-0 bold text-decoration-none zocks-text-primary'
                loading={isSelectingAll}
                onClick={handleSelectAll}
              >
                Select all
              </Button>
            </div>
          )}
        </div>
      }
      showFooterActions
      footerPrimaryActionTitle={isSingle ? 'Save host' : 'Save hosts'}
      footerSecondaryActionTitle='Cancel'
      footerPrimaryActionDisabled={!canSave || isSelectingAll}
      onFooterPrimaryAction={() => onSave(selectedHosts)}
      onFooterSecondaryAction={onCancel}
    />
  )
}
